import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Mail, Send, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { HreflangTags } from "@/components/HreflangTags";
import { useLanguage } from "@/contexts/LanguageContext";
import { contactTranslations } from "@/i18n/contactTranslations";

export default function ContactPage() {
  const { toast } = useToast();
  const { language } = useLanguage();
  const t = contactTranslations[language];
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    message: "",
    inquiryType: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast({
      title: t.form.successTitle,
      description: t.form.successDescription,
    });

    // Reset form
    setFormData({
      name: "",
      email: "",
      company: "",
      phone: "",
      subject: "",
      message: "",
      inquiryType: ""
    });

    setIsSubmitting(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <Helmet>
        <title>Contact Ranvir Gill & CheckFirst - Healthcare Triage Software</title>
        <meta name="description" content="Contact Ranvir Gill and the CheckFirst team for inquiries about our telehealth triage platform. Healthcare technology solutions from the founder of CheckFirst." />
        <meta name="keywords" content="contact Ranvir Gill, Ranvir Gill CheckFirst, telehealth triage platform, hospital digital intake solution, ER patient flow software, healthcare technology company, patient triage platform" />
        <meta name="author" content="Ranvir Gill" />
        
        <meta property="og:title" content="Contact Ranvir Gill & CheckFirst - Healthcare Triage Software" />
        <meta property="og:description" content="Contact Ranvir Gill and the CheckFirst team for inquiries about our telehealth triage platform and healthcare technology solutions." />
        <meta property="og:url" content="https://www.checkfirstcare.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.checkfirstcare.com/og-image.jpg" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Ranvir Gill & CheckFirst - Healthcare Triage Software" />
        <meta name="twitter:description" content="Contact Ranvir Gill and the CheckFirst team for inquiries about our telehealth triage platform." />
        
        <link rel="canonical" href="https://www.checkfirstcare.com/contact" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact CheckFirst",
            "description": "Contact Ranvir Gill and the CheckFirst team for healthcare technology inquiries",
            "url": "https://www.checkfirstcare.com/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "CheckFirst",
              "founder": {
                "@type": "Person",
                "name": "Ranvir Gill",
                "jobTitle": "Founder & CEO"
              }
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.checkfirstcare.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Contact",
                "item": "https://www.checkfirstcare.com/contact"
              }
            ]
          })}
        </script>
      </Helmet>
      <HreflangTags />
      
      <div className="min-h-screen">
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 lg:px-8 pt-6">
          <Breadcrumbs items={[{ label: 'Contact' }]} />
        </div>

        {/* Hero Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-4xl lg:text-6xl font-bold text-foreground leading-tight">
              {t.hero.title} <span className="text-primary">{t.hero.titleHighlight}</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              {t.hero.subtitle}{" "}
              <Link to="/about" className="text-primary hover:underline font-medium">{t.hero.teamLink}</Link>{" "}
              {language === 'hi' && 'और हमारी टीम से।'}
              {language === 'pa' && 'ਅਤੇ ਸਾਡੀ ਟੀਮ ਨਾਲ।'}
              {language === 'bn' && 'এবং আমাদের টিমের সাথে।'}
              {language === 'es' && 'y nuestro equipo.'}
              {language === 'en' && 'and our team.'}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section id="contact-form" className="py-20 lg:py-32 bg-secondary/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-2xl">{t.form.cardTitle}</CardTitle>
                  <p className="text-muted-foreground">
                    {t.form.cardSubtitle}
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">{t.form.fullName}</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleInputChange("name", e.target.value)}
                          required
                          placeholder={t.form.fullNamePlaceholder}
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">{t.form.email}</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          required
                          placeholder={t.form.emailPlaceholder}
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="company">{t.form.company}</Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => handleInputChange("company", e.target.value)}
                          placeholder={t.form.companyPlaceholder}
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">{t.form.phone}</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          placeholder={t.form.phonePlaceholder}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="inquiryType">{t.form.inquiryType}</Label>
                      <Select onValueChange={(value) => handleInputChange("inquiryType", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder={t.form.inquiryTypePlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">{t.form.inquiryOptions.general}</SelectItem>
                          <SelectItem value="partnerships">{t.form.inquiryOptions.partnerships}</SelectItem>
                          <SelectItem value="healthcare-provider">{t.form.inquiryOptions.healthcareProvider}</SelectItem>
                          <SelectItem value="insurance">{t.form.inquiryOptions.insurance}</SelectItem>
                          <SelectItem value="technical">{t.form.inquiryOptions.technical}</SelectItem>
                          <SelectItem value="media">{t.form.inquiryOptions.media}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="subject">{t.form.subject}</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => handleInputChange("subject", e.target.value)}
                        required
                        placeholder={t.form.subjectPlaceholder}
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">{t.form.message}</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange("message", e.target.value)}
                        required
                        placeholder={t.form.messagePlaceholder}
                        className="min-h-32"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      variant="hero" 
                      size="lg" 
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          {t.form.sending}
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          {t.form.sendMessage}
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              
              {/* General Info */}
              <Card className="shadow-card">
                <CardContent className="p-6 space-y-6">
                  <h3 className="text-xl font-semibold text-foreground">{t.sidebar.getStarted}</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 gradient-hero rounded-lg flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{t.sidebar.aiAssessment}</p>
                        <p className="text-muted-foreground text-sm">{t.sidebar.aiAssessmentDesc}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 gradient-success rounded-lg flex items-center justify-center">
                        <Mail className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{t.sidebar.contactForm}</p>
                        <p className="text-muted-foreground text-sm">{t.sidebar.contactFormDesc}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Response Time */}
              <Card className="shadow-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-3">{t.sidebar.responseTime}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t.sidebar.generalInquiries}</span>
                      <span className="text-foreground font-medium">{t.sidebar.hours24to48}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t.sidebar.partnershipInquiries}</span>
                      <span className="text-foreground font-medium">{t.sidebar.days2to3}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t.sidebar.technicalSupport}</span>
                      <span className="text-foreground font-medium">{t.sidebar.within24hours}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 gradient-hero">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <div className="space-y-8 max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-white">
              {t.cta.title}
            </h2>
            <p className="text-xl text-white/90">
              {t.cta.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="secondary" 
                size="lg"
                onClick={() => {
                  document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t.cta.scheduleDemo}
              </Button>
            </div>
          </div>
        </div>
        </section>
      </div>
    </>
  );
}
